// src/theme/createComponents.js
import { alpha } from "@mui/material/styles";

export function createComponents(theme) {
  const isDark = theme.palette.mode === "dark";

  const glassSurface =
    theme.custom?.glass?.surface ??
    (isDark ? "rgba(16, 18, 23, 0.72)" : "rgba(255,255,255,0.72)");

  const glassBorder =
    theme.custom?.glass?.border ??
    (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)");

  const paperBg = theme.palette.background.paper;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
      },
    },

            MuiPaper: {
          styleOverrides: {
              root: {
                  backgroundImage: "none",
                  border: "none",
                  ...(theme.palette.mode === "light"
                      ? {
                            backgroundColor: theme.custom?.glass?.surface || "rgba(255,255,255,0.72)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                        }
                      : {
                            backgroundColor: theme.palette.background.paper,
                        }),
              },
          },
      },

      MuiCard: {
          styleOverrides: {
              root: {
                  border: "none",
                  ...(theme.palette.mode === "light"
                      ? {
                            backgroundColor: theme.custom?.glass?.surface || "rgba(255,255,255,0.72)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                        }
                      : {
                            backgroundColor: theme.palette.background.paper,
                        }),
              },
          },
      },

      MuiDialog: {
          styleOverrides: {
              paper: {
                  border: "none",
                  ...(theme.palette.mode === "light"
                      ? {
                            backgroundColor: theme.custom?.glass?.strongSurface || "rgba(255,255,255,0.86)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                        }
                      : {
                            backgroundColor: theme.palette.background.paper,
                        }),
              },
          },
      },

      MuiMenu: {
          styleOverrides: {
              paper: {
                  ...(theme.palette.mode === "light"
                      ? { backgroundColor: theme.custom?.glass?.strongSurface || "rgba(255,255,255,0.86)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }
                      : { backgroundColor: theme.palette.background.paper, backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }),
                  border: "none",
              },
          },
      },


    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: theme.custom?.glass?.strongSurface ?? paperBg,
          border: 'none',
        },
      },
    },

    // ✅ Inputs: fix faint text/placeholder globally
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
        },
        input: {
          color: theme.palette.text.primary,
          caretColor: theme.palette.primary.main,
          "&::placeholder": {
            color: theme.palette.text.secondary,
            opacity: 1,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: "transparent",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: glassBorder,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.text.primary, 0.30),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.primary.main, 0.75),
          },
        },
      },
    },

    MuiInput: {
      styleOverrides: {
        underline: {
          "&:before": {
            borderBottomColor: glassBorder,
          },
          "&:hover:not(.Mui-disabled):before": {
            borderBottomColor: alpha(theme.palette.text.primary, 0.30),
          },
          "&:after": {
            borderBottomColor: alpha(theme.palette.primary.main, 0.75),
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },

      MuiAppBar: {
          styleOverrides: {
              root: {
                  backgroundImage: "none",
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: "none",
              },
          },
      },


      MuiBottomNavigation: {
          styleOverrides: {
              root: {
                  backgroundColor: glassSurface,
                  borderTop: "none",
              },
          },
      },


    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: theme.palette.text.secondary,
          "&.Mui-selected": {
            color: theme.palette.primary.main,
          },
        },
        label: {
          fontSize: "0.68rem",
          "&.Mui-selected": {
            fontSize: "0.68rem",
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: alpha(theme.palette.divider, 0.9),
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.custom?.glass?.strongSurface ?? paperBg,
          border: `1px solid ${glassBorder}`,
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        },
      },
    },
  };
}
